/**
 * Iter 67br — Activity log viewer.
 *
 * Renders a paginated, filterable view of the vendor's audit trail. Used by:
 *   - Vendor dashboard "Activity log" tab          → endpoint=/vendor/audit-logs
 *   - Admin dashboard inspecting a vendor          → endpoint=/admin/vendors/{id}/audit-logs
 *
 * Filters: entity_type, action, actor, date range. All optional; defaults
 * render the last 100 events with newest first.
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, RotateCcw, Activity, ShieldCheck, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const ENTITY_OPTIONS = [
  { value: "all", label: "All entities" },
  { value: "booking", label: "Bookings" },
  { value: "invoice", label: "Invoices" },
  { value: "agreement", label: "Agreements" },
  { value: "payment", label: "Payments" },
  { value: "vehicle", label: "Vehicles" },
  { value: "price", label: "Prices" },
  { value: "availability", label: "Availability" },
  { value: "customer", label: "Customers" },
  { value: "refund", label: "Refunds" },
  { value: "staff", label: "Staff" },
  { value: "settings", label: "Settings" },
];

const ACTION_OPTIONS = [
  { value: "all", label: "All actions" },
  { value: "create", label: "Created" },
  { value: "update", label: "Edited" },
  { value: "cancel", label: "Cancelled" },
  { value: "delete", label: "Deleted" },
  { value: "approve", label: "Approved" },
  { value: "refund", label: "Refunded" },
  { value: "activate", label: "Activated" },
  { value: "deactivate", label: "Deactivated" },
  { value: "void", label: "Voided" },
  { value: "send", label: "Sent" },
  { value: "sign", label: "Signed" },
  { value: "block", label: "Blocked" },
  { value: "unblock", label: "Unblocked" },
];

const ACTION_TONE = {
  create:     "bg-emerald-100 text-emerald-800 ring-emerald-200",
  update:     "bg-blue-100 text-blue-800 ring-blue-200",
  cancel:     "bg-rose-100 text-rose-800 ring-rose-200",
  delete:     "bg-rose-100 text-rose-800 ring-rose-200",
  approve:    "bg-violet-100 text-violet-800 ring-violet-200",
  refund:     "bg-amber-100 text-amber-800 ring-amber-200",
  activate:   "bg-emerald-100 text-emerald-800 ring-emerald-200",
  deactivate: "bg-neutral-100 text-neutral-700 ring-neutral-200",
  void:       "bg-rose-100 text-rose-800 ring-rose-200",
  send:       "bg-sky-100 text-sky-800 ring-sky-200",
  sign:       "bg-violet-100 text-violet-800 ring-violet-200",
  block:      "bg-amber-100 text-amber-800 ring-amber-200",
  unblock:    "bg-emerald-100 text-emerald-800 ring-emerald-200",
};

function _fmtTs(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso.slice(0, 16).replace("T", " ");
  }
}

export default function VendorActivityLog({ endpoint = "/vendor/audit-logs", title = "Activity log", subtitle }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entity, setEntity] = useState("all");
  const [action, setAction] = useState("all");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (entity !== "all") params.entity_type = entity;
      if (action !== "all") params.action = action;
      if (start) params.start = start;
      if (end) params.end = end;
      const { data } = await api.get(endpoint, { params });
      setRows(data?.rows || []);
    } catch (e) {
      toast.error(formatApiError(e) || "Could not load activity log");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [endpoint, entity, action, start, end]);

  const reset = () => {
    setEntity("all"); setAction("all"); setStart(""); setEnd("");
  };

  const grouped = useMemo(() => {
    const byDay = new Map();
    for (const r of rows) {
      const day = (r.created_at || "").slice(0, 10) || "—";
      if (!byDay.has(day)) byDay.set(day, []);
      byDay.get(day).push(r);
    }
    return Array.from(byDay.entries());
  }, [rows]);

  return (
    <div className="space-y-4" data-testid="vendor-activity-log">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-white shadow-md shadow-violet-200">
            <Activity className="h-4 w-4" />
          </div>
          <div>
            <h2 className="font-display text-2xl font-semibold tracking-tight">{title}</h2>
            {subtitle && <p className="text-sm text-neutral-500 mt-0.5">{subtitle}</p>}
          </div>
        </div>
        <Button variant="ghost" onClick={load} className="rounded-full" data-testid="vendor-activity-refresh">
          <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 rounded-2xl border border-border bg-white p-3">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">Entity</Label>
          <Select value={entity} onValueChange={setEntity}>
            <SelectTrigger className="mt-1" data-testid="vendor-activity-entity"><SelectValue /></SelectTrigger>
            <SelectContent>{ENTITY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">Action</Label>
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger className="mt-1" data-testid="vendor-activity-action"><SelectValue /></SelectTrigger>
            <SelectContent>{ACTION_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">From</Label>
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1" data-testid="vendor-activity-start" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">To</Label>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1" data-testid="vendor-activity-end" />
        </div>
        <div className="sm:col-span-2 lg:col-span-4 flex justify-end">
          <Button variant="ghost" onClick={reset} size="sm" className="rounded-full text-neutral-500">
            <RotateCcw className="h-3.5 w-3.5 mr-1" /> Clear filters
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="rounded-2xl border border-border bg-white p-16 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-12 text-center" data-testid="vendor-activity-empty">
          <div className="mx-auto h-12 w-12 rounded-2xl bg-violet-100 flex items-center justify-center mb-3">
            <ShieldCheck className="h-5 w-5 text-violet-700" />
          </div>
          <div className="font-semibold text-neutral-900">Nothing matches your filters</div>
          <div className="text-sm text-neutral-500 mt-1">Try clearing the filters or widening the date range.</div>
        </div>
      ) : (
        <div className="space-y-5">
          {grouped.map(([day, items]) => (
            <div key={day}>
              <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold mb-2">{day}</div>
              <div className="rounded-2xl border border-border bg-white overflow-hidden">
                {items.map((r, idx) => (
                  <div
                    key={r.id || idx}
                    className={`flex items-start gap-3 px-4 py-3 ${idx > 0 ? "border-t border-border" : ""}`}
                    data-testid={`vendor-activity-row-${r.id}`}
                  >
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ring-1 text-[10px] font-bold uppercase tracking-wider shrink-0 ${ACTION_TONE[r.action] || "bg-neutral-100 text-neutral-700 ring-neutral-200"}`}>
                      {r.action}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm">
                        <span className="font-semibold text-neutral-900">{r.actor_name}</span>{" "}
                        <span className="text-neutral-500">{r.action}d</span>{" "}
                        <span className="font-semibold text-neutral-900">{r.entity_type}</span>{" "}
                        {r.entity_label && <span className="text-neutral-600">— {r.entity_label}</span>}
                      </div>
                      {r.changes && (
                        <div className="text-[11px] text-neutral-500 mt-0.5 truncate" title={JSON.stringify(r.changes)}>
                          {Object.keys(r.changes).slice(0, 4).join(", ")}{Object.keys(r.changes).length > 4 ? "…" : ""}
                        </div>
                      )}
                    </div>
                    <div className="text-[11px] text-neutral-500 tabular-nums shrink-0">{_fmtTs(r.created_at)}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
